export const TEXT_COLOR = '#1f2a44';
export const BUTTON_TEXT = '#ffffff';
export const CURRENT_PAGE_BG = 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)';

export const THEME_PRESETS = [
  {
    id: 'default',
    labelKey: 'themeDefault',
    primaryColor: '#3965ac',
    secondaryColor: '#be9830',
    pageBackground: ''
  },
  {
    id: 'green-gold',
    labelKey: 'themeGreenGold',
    primaryColor: '#1B6B4A',
    secondaryColor: '#C4A35A',
    pageBackground: '#F4F7F5'
  },
  {
    id: 'navy-copper',
    labelKey: 'themeNavyCopper',
    primaryColor: '#1E3A5F',
    secondaryColor: '#B87333',
    pageBackground: '#F4F6F8'
  },
  {
    id: 'teal-sand',
    labelKey: 'themeTealSand',
    primaryColor: '#0F6E6E',
    secondaryColor: '#C09A64',
    pageBackground: '#F3F7F7'
  },
  {
    id: 'burgundy-gold',
    labelKey: 'themeBurgundyGold',
    primaryColor: '#7A2E3A',
    secondaryColor: '#C6A15B',
    pageBackground: '#F8F5F5'
  },
  {
    id: 'slate-blue',
    labelKey: 'themeSlateBlue',
    primaryColor: '#3D4A5C',
    secondaryColor: '#4C86B0',
    pageBackground: '#F4F6F8'
  }
];

const CURRENT = THEME_PRESETS[0];

export const normalizeHex = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
  if (!match) return '';
  return `#${match[1].toLowerCase()}`;
};

const channel = (value) => {
  const scaled = value / 255;
  return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
};

const luminance = (hex) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return 0;
  const r = channel(parseInt(normalized.slice(1, 3), 16));
  const g = channel(parseInt(normalized.slice(3, 5), 16));
  const b = channel(parseInt(normalized.slice(5, 7), 16));
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
};

export const contrastRatio = (foreground, background) => {
  const lighter = Math.max(luminance(foreground), luminance(background));
  const darker = Math.min(luminance(foreground), luminance(background));
  return (lighter + 0.05) / (darker + 0.05);
};

export const darkenHex = (hex, amount = 0.18) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return CURRENT.primaryColor;
  if (normalized === '#3965ac') return '#2d4f8a';
  const mix = (value) => Math.max(0, Math.min(255, Math.round(value * (1 - amount))));
  const r = mix(parseInt(normalized.slice(1, 3), 16));
  const g = mix(parseInt(normalized.slice(3, 5), 16));
  const b = mix(parseInt(normalized.slice(5, 7), 16));
  return `#${[r, g, b].map((part) => part.toString(16).padStart(2, '0')).join('')}`;
};

export const chevronImage = (hex) => {
  const color = (normalizeHex(hex) || CURRENT.primaryColor).replace('#', '%23');
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${color}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`;
};

export const themeFromColors = (primaryColor, secondaryColor, pageBackground) => {
  const primary = normalizeHex(primaryColor);
  const secondary = normalizeHex(secondaryColor);
  const background = normalizeHex(pageBackground);
  const match = THEME_PRESETS.find((preset) => (
    normalizeHex(preset.primaryColor) === primary
    && normalizeHex(preset.secondaryColor) === secondary
    && normalizeHex(preset.pageBackground) === background
  ));
  return match ? match.id : 'custom';
};

export const validateThemeColors = ({ primaryColor, secondaryColor, pageBackground }) => {
  const primary = normalizeHex(primaryColor) || CURRENT.primaryColor;
  const secondary = normalizeHex(secondaryColor) || CURRENT.secondaryColor;
  const background = normalizeHex(pageBackground);
  if (contrastRatio(BUTTON_TEXT, primary) < 4.5) return 'primary';
  if (contrastRatio(BUTTON_TEXT, secondary) < 2.35) return 'secondary';
  if (background && contrastRatio(TEXT_COLOR, background) < 4.5) return 'background';
  return '';
};

export const applyThemeVariables = (theme) => {
  if (typeof document === 'undefined') return;
  const primary = normalizeHex(theme?.primaryColor) || CURRENT.primaryColor;
  const secondary = normalizeHex(theme?.secondaryColor) || CURRENT.secondaryColor;
  const customBackground = normalizeHex(theme?.pageBackground);
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-primary-hover', darkenHex(primary));
  root.style.setProperty('--brand-secondary', secondary);
  root.style.setProperty('--brand-chevron', chevronImage(primary));

  if (customBackground) {
    root.style.setProperty('--brand-page-bg', customBackground);
    root.style.setProperty('--brand-admin-bg', customBackground);
  } else {
    root.style.setProperty('--brand-page-bg', CURRENT_PAGE_BG);
    root.style.removeProperty('--brand-admin-bg');
  }

  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute('content', primary);
};
